// Sprint 12.5 — capability-aware App Management Shell. Lives in the creator
// Workspace (App Detail), NEVER inside the generated customer app — this is the
// owner/creator side of the customer/owner boundary.
//
// §S680 — the shell holds ONLY capability management panels. Edit / Analytics /
// Open&Share rows were removed: App Detail owns the single action row and the
// stat band is the analytics surface, so no action ever appears twice. Panels
// that lack a server-backed management API render an HONEST "not wired yet"
// state (no fake rows), all strings via t().
import type { JSX } from 'preact';
import { Utensils, MapPin, MessageSquare, Sparkles, Lock } from 'lucide-preact';
import type { LucideIcon } from 'lucide-preact';
import { t } from '../lib/i18n';

export type AppCapability =
  | 'booking' | 'commerce' | 'orderahead' | 'crm' | 'tracker' | 'directory' | 'community' | 'ai'
  | 'ticketing' | 'inventory' | 'quizzes' | 'events' | 'hr'
  | 'generic';

/** Best-effort capability inference from the app's own text (name + prompt).
 *  FALLBACK ONLY — the /v1/creator/apps rows carry no category field yet, so
 *  word-boundary regexes keep 'bookstore' out of booking and 'ai' out of 'Thai'. */
export function inferCapability(text: string): AppCapability {
  const s = (text || '').toLowerCase();
  if (/\bbook(ing|ings)?\b|\bappointments?\b|\bsalons?\b|\bbarbers?\b|\bspa\b|\breserv(e|es|ation|ations)\b|\bstylists?\b|\bclinics?\b/.test(s)) return 'booking';
  if (/\bshops?\b|\bstores?\b|\bproducts?\b|\bcarts?\b|\bcheckout\b|\becommerce\b|\be-commerce\b|\bboutiques?\b/.test(s)) return 'commerce';
  if (/\bmenus?\b|\brestaurants?\b|\border ahead\b|\btakeout\b|\bcafes?\b|café|\bfood trucks?\b|\bkitchens?\b/.test(s)) return 'orderahead';
  // §Owner-console generalization — owner-managed categories (each has a ManageRecords console below).
  if (/\binventory\b|\bstockroom\b|\bstock (levels?|counts?|room)\b|\bwarehouse\b|\bskus?\b|\bsupplies\b/.test(s)) return 'inventory';
  if (/\bevents?\b|\brsvps?\b|\bmeetups?\b|\bwebinars?\b|\bconferences?\b/.test(s)) return 'events';
  if (/\bhelp ?desk\b|\bsupport (desk|queue|tickets?)\b|\btickets?\b|\bissue tracker\b|\bservice requests?\b/.test(s)) return 'ticketing';
  if (/\bquiz(zes)?\b|\bassessments?\b|\bexams?\b/.test(s)) return 'quizzes';
  if (/\bhr\b|\bhuman resources?\b|\bemployees?\b|\btime ?off\b|\bpto\b|\bleave requests?\b/.test(s)) return 'hr';
  if (/\bleads?\b|\bpipelines?\b|\bdeals?\b|\bcrm\b|\bsales\b/.test(s)) return 'crm';
  if (/\btrack(er|ers|ing)?\b|\bhabits?\b|\blogs?\b|\bworkouts?\b|\bexpenses?\b|\bstreaks?\b|\bprogress\b/.test(s)) return 'tracker';
  if (/\bdirectory\b|\blistings?\b|\bplaces\b|\bfield guide\b|\bcatalog of\b/.test(s)) return 'directory';
  if (/\bfeeds?\b|\bcommunity\b|\bsocial\b|\bposts?\b/.test(s)) return 'community';
  if (/\bai\b|\bassistants?\b|\bchatbots?\b|\bgenerate\b|\bsummar\w*|\bcopywrit\w*/.test(s)) return 'ai';
  return 'generic';
}

/** One owner-managed collection whose contract-driven ManageRecords console the host mounts. */
export interface OwnerConsoleDef { collection: string; titleKey: string; }

/** DATA-DRIVEN owner-console map: for each capability, the owner-managed collection(s) whose real
 *  server-backed console (list + lifecycle transitions + CSV export) the host renders. Every
 *  collection here is `ownerWritable` in the shared collection contracts, so the generic owner route
 *  (/v1/apps/:appId/owner/:collection) serves it — a non-owner gets an empty list, never a leak.
 *  This replaces the old hard-coded per-capability allowlist so ticketing / inventory / quizzes /
 *  events / hr / directory owners get a console the same way orderahead/commerce always did. Booking
 *  keeps its bespoke panels (ManageBookings et al.), so it maps to no generic console. */
export const CAPABILITY_OWNER_CONSOLES: Record<AppCapability, OwnerConsoleDef[]> = {
  booking: [],
  commerce: [{ collection: 'orders', titleKey: 'mgmt.ord.title' }],
  orderahead: [{ collection: 'orders', titleKey: 'mgmt.ord.title' }],
  crm: [],
  tracker: [],
  directory: [
    { collection: 'listing_submissions', titleKey: 'mgmt.submissions.title' }, // §DEAD-CTRL-FIX — review visitor submissions; approve promotes into a canonical listing
    { collection: 'listings', titleKey: 'mgmt.listings.title' },
  ],
  community: [],
  ai: [],
  ticketing: [{ collection: 'tickets', titleKey: 'mgmt.tickets.title' }],
  inventory: [
    { collection: 'items', titleKey: 'mgmt.items.title' },
    { collection: 'adjustments', titleKey: 'mgmt.adjustments.title' },
  ],
  quizzes: [{ collection: 'attempts', titleKey: 'mgmt.attempts.title' }],
  events: [{ collection: 'rsvps', titleKey: 'mgmt.rsvps.title' }],
  hr: [
    { collection: 'timeoff', titleKey: 'mgmt.timeoff.title' },
    { collection: 'employees', titleKey: 'mgmt.employees.title' },
  ],
  generic: [],
};

/** True when a capability has at least one generic owner-managed console (excludes booking's bespoke
 *  panels). Hosts use this to suppress the honest "not wired yet" stub once a real console is shown. */
export function hasOwnerConsole(capability: AppCapability): boolean {
  return (CAPABILITY_OWNER_CONSOLES[capability]?.length ?? 0) > 0;
}

interface PanelDef { key: string; Icon: LucideIcon; titleKey: string; subKey: string; }
const CAPABILITY_PANEL: Record<AppCapability, PanelDef | null> = {
  // §SEP-2 / §DEF-OWNERCONSOLE-1 (2026-07-12) — the LIVE host v2/AppHubV2.tsx mounts each of these
  // REAL owner surfaces directly, so none shows an honest "not wired yet" stub (null here): booking →
  // ManageBookings / ManageCustomers / BookingAvailability / PaymentLinkConfig; commerce → the generic
  // orders ManageRecords console (its owner_console contract = orders + status + server-price, already
  // complete); crm → ManageCRM (contacts + deals CRUD + DEAL_LIFECYCLE pipeline + activity timeline,
  // satisfying the manage_customers contract). The OTHER advanced consoles (ManageProducts /
  // ManageInventory / ShopAnalytics / ManageServices / ManageResources / BookingAnalytics) are
  // out-of-contract and live only in the unrouted screens/AppDetail.tsx — dead code pending deletion
  // after dependency proof (defect_ledger DEF-OWNERCONSOLE-1).
  booking: null,
  commerce: null,
  orderahead: { key: 'orderahead', Icon: Utensils, titleKey: 'ad.mgOrderT', subKey: 'ad.mgOrderS' },
  crm: null,
  // §S7-NOTWIRED (Session 7 closure) — a personal tracker has no business-management surface, and
  // showing a locked "Entries & analytics — review logged entries and trends" promised one that was
  // never coming. The entries and the trends belong to the person who logged them and are IN the app
  // (§S7-FIELD-PLAN renders its figures, its states and its trend from the same records); a console
  // listing every end-user's private logs is exactly the customer/owner mixing the separation law
  // forbids. Nothing to manage is the truth here, so the shell says nothing rather than promising.
  tracker: null,
  directory: { key: 'directory', Icon: MapPin, titleKey: 'ad.mgDirectoryT', subKey: 'ad.mgDirectoryS' },
  community: { key: 'community', Icon: MessageSquare, titleKey: 'ad.mgCommunityT', subKey: 'ad.mgCommunityS' },
  ai: { key: 'ai', Icon: Sparkles, titleKey: 'ad.mgAiT', subKey: 'ad.mgAiS' },
  // Owner-managed categories render their REAL ManageRecords console (host-mounted), so they never
  // show the honest "not wired yet" stub — null here, exactly like booking/orderahead when wired.
  ticketing: null,
  inventory: null,
  quizzes: null,
  events: null,
  hr: null,
  generic: null
};

export function AppManagementShell({ capability, bookingWired, ordersWired }: {
  capability: AppCapability;
  /** Sprint 7 — when the real server-backed Manage Bookings panel is rendered by
   *  the host (App Detail), suppress the honest "not wired yet" booking stub. */
  bookingWired?: boolean;
  /** §S2 — when the real Owner Order Console (ManageRecords) is rendered by the host
   *  for orderahead, suppress the (now-false) "not wired yet" orders stub. */
  ordersWired?: boolean;
}): JSX.Element | null {
  // The real panel is rendered separately by the host; don't also show the not-wired stub for a
  // capability whose real management surface is now live — booking/orderahead (wired flags) OR any
  // capability with a generic owner console (directory/commerce/…), whose ManageRecords the host mounts.
  const wired = (capability === 'booking' && bookingWired) || (capability === 'orderahead' && ordersWired) || hasOwnerConsole(capability);
  const capPanel = wired ? null : CAPABILITY_PANEL[capability];
  if (!capPanel) return null;
  return (
    <div className="pf-mgmt">
      {/* CAPABILITY panel — honest not-wired state. NEVER fabricates rows. */}
      <div className="pf-mgmt-panel" role="group" aria-label={t(capPanel.titleKey)}>
        <span className="pf-mgmt-panel__icon"><capPanel.Icon size={18} /></span>
        <span className="pf-mgmt-panel__body">
          <span className="pf-mgmt-panel__t">{t(capPanel.titleKey)}</span>
          <span className="pf-mgmt-panel__s">{t(capPanel.subKey)}</span>
        </span>
        <span className="pf-notwired" title={t('ad.notWiredHint')}>
          <Lock size={12} /> {t('ad.notWired')}
        </span>
      </div>
    </div>
  );
}
